import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import DataLoadError from '@/components/errors/DataLoadError';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { BrandFavicon } from '../BrandFavicon';
import { RelationshipBadge } from '../visibility/VisibilityBadges';
import { rightDrawerAsideClass, rightDrawerOverlayClass, rightDrawerZIndex } from '../../utils/panel-layout';
import {
  formatBrandSourcesEmptyState,
  formatSourceCount,
  formatSourceDisplayType,
} from '../../utils/source-insights';
import type { DateRangeQueryOptions } from '../../utils/visibility-range';
import type { BrandSourceInsightsResult } from '../../types';
import type { DomainBrandLike } from '../../utils/domain-filter-utils';
import { brandKey } from '../../utils/domain-filter-utils';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { brandSourcesColumnConfig } from '../../config/brandSourcesColumnConfig';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  settings?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function BrandSourcesDrawer({
  open,
  brand,
  projectId,
  rangeQuery,
  onClose,
}: {
  open: boolean;
  brand: DomainBrandLike | null;
  projectId: string;
  rangeQuery: DateRangeQueryOptions;
  onClose: () => void;
}) {
  const [data, setData] = useState<BrandSourceInsightsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const resolvedBrandKey = brand ? brandKey(brand) : null;
  const queryKey = useMemo(() => JSON.stringify(rangeQuery), [rangeQuery]);
  const brandSourcesColumns = useMemo(() => brandSourcesColumnConfig(), []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open || !projectId || !resolvedBrandKey) return;
    let cancelled = false;
    setLoading(true);

    api
      .getBrandSourceInsights(projectId, resolvedBrandKey, rangeQuery)
      .then(result => {
        if (!cancelled) {
          setData(result);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectId, resolvedBrandKey, queryKey, rangeQuery, retryNonce]);

  if (!open || !brand) return null;

  const titleName = data?.brand.canonicalName ?? brand.canonicalName;

  return (
    <div className={rightDrawerOverlayClass} style={{ zIndex: rightDrawerZIndex }}>
      <button
        type="button"
        className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-50 border-0"
        aria-label="بستن منابع برند"
        onClick={onClose}
      />
      <aside className={rightDrawerAsideClass} style={{ maxWidth: 720 }} role="dialog" aria-label="منابع برند">
        <div className="border-bottom px-4 py-3">
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div className="min-w-0">
              <h2 className="fs-1 fw-semibold text-1000 mb-0">منابع برند</h2>
              <p className="mt-1 fs--1 text-500 mb-0">
                منابعی که در پاسخ‌های AI همراه این برند دیده شده‌اند
              </p>
              <div className="mt-3 d-flex align-items-center gap-2">
                <BrandFavicon host={brand.domain} faviconUrl={undefined} name={titleName} />
                <div>
                  <p className="fw-medium text-1000 mb-0">{titleName}</p>
                  {data?.brand.relationship && <RelationshipBadge relationship={data.brand.relationship} />}
                </div>
              </div>
            </div>
            <Button type="button" size="sm" variant="falcon-default" onClick={onClose}>
              بستن
            </Button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto p-4">
          {error ? (
            <DataLoadError
              compact
              onRetry={() => setRetryNonce(n => n + 1)}
              isRetrying={loading}
            />
          ) : loading ? (
            <AgGridSkeleton height="360px" />
          ) : !data || data.sources.length === 0 ? (
            <div className="text-center py-5">
              <p className="fs--1 fw-medium text-700 mb-0">{formatBrandSourcesEmptyState()}</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-4">
              <Row className="g-3 row-cols-2 row-cols-lg-4">
                <Col>
                  <SummaryCard label="منابع" value={formatSourceCount(data.summary.totalSources)} />
                </Col>
                <Col>
                  <SummaryCard label="Citationها" value={formatSourceCount(data.summary.totalCitations)} />
                </Col>
                <Col>
                  <SummaryCard
                    label="پاسخ‌های AI"
                    value={formatSourceCount(data.summary.responsesWithSources)}
                  />
                </Col>
                <Col>
                  <SummaryCard
                    label="نوع غالب"
                    value={formatSourceDisplayType(data.summary.topDisplayType)}
                  />
                </Col>
              </Row>

              <Card className="mb-0">
                <TypedAgGridWrapper
                  table_name="tracker_ai_brand_sources"
                  height="auto"
                  columns={brandSourcesColumns}
                  data={data.sources}
                  total={data.sources.length}
                  settings={{ pagination: false, fullscreen: true, sideBar: false, export: { enabled: false } }}
                />
              </Card>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="h-100">
      <Card.Body className="p-3">
        <p className="fs--1 text-500 mb-0">{label}</p>
        <p className="mt-1 fs-0 fw-semibold text-1000 mb-0">{value}</p>
      </Card.Body>
    </Card>
  );
}
